import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Button,
  Chip,
  Tab,
  Tabs,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import { Container, Row, Accordion } from "react-bootstrap";
import { toast } from "react-toastify";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GetAppIcon from '@mui/icons-material/GetApp';
import {
  ACCEPTED_EXTENSIONS,
  ActiveTab,
  ActiveTabEnum,
  BreakReasonItem,
  BuySell,
  ReconFilterFormData,
  ReconRunResponse,
  ReconSourceEnum,
  ReconStatusEnum,
  ReconSummary,
  ReconTradeRow,
  SelectOption,
  SidedTableConfig,
} from "../../../Types/Reconautomation.types";
import {
  fetchBrokerOptions,
  fetchContractOptions,
  runReconciliation,
} from "./reconautomation.api";
import {
  CompactSelect,
  CompactTextField,
} from "../../../Components/SharedComponents/SharedComponents";
import TradeDetailModal from "./TradeDetailModal";
import { BrokerOption } from "../../ApiSettings/ApiBrokerSettings";
import { DateFormatErrorModal, ValidationError } from "./dateFormateErrorModal";

const BUY_SELL_OPTIONS: SelectOption[] = [
  { label:"All", value: "" },
  { label: "Buy", value: "buy" },
  { label: "Sell", value: "sell" },
];
const CONTRACT_ALL_OPTION: SelectOption = { label: "\u200B All", value: "" };

const DEFAULT_SOURCE = "Britannia" as const;

// ─── Constants

const INITIAL_FILTERS: ReconFilterFormData = {
  source: DEFAULT_SOURCE,
  start_date: "",
  end_date: "",
  contract: "",
  buy_sell: "",
};

const STATUS_COLOR: Record<string, "success" | "error" | "warning" | "default"> = {
  MATCHED: "success",
  MISMATCH_PRICE: "error",
  MISMATCH_MARKUP: "warning",
  MISMATCH_PRICE_AND_MARKUP: "error",
  UNMATCHED_BGM: "warning",
  UNMATCHED_ACERS: "warning",
  CANON_FAILED: "default",
};

const STATUS_LABEL: Record<string, string> = {
  MATCHED: "Matched",
  MISMATCH_PRICE: "Price Mismatch",
  MISMATCH_MARKUP: "Markup Mismatch",
  MISMATCH_PRICE_AND_MARKUP: "Price & Markup",
  UNMATCHED_BGM: "Unmatched BGM",
  UNMATCHED_ACERS: "Unmatched ACERS",
  CANON_FAILED: "Canon Failed",
};

const PAIRED_STATUSES = new Set([
  ReconStatusEnum.MATCHED,
  "MISMATCH_PRICE",
  "MISMATCH_MARKUP", 
  "MISMATCH_PRICE_AND_MARKUP",
]);

const SUMMARY_CARDS: {
  key: keyof ReconSummary;
  label: string;
  accent: string;
}[] = [
    { key: "total_bgm_trades", label: "BGM Trades", accent: "#1565c0" },
    { key: "total_acers_trades", label: "ACERS Trades", accent: "#1565c0" },
    { key: "matched", label: "Matched", accent: "#2e7d32" },
    { key: "mismatch_price", label: "Price Mismatch", accent: "#c62828" },
    { key: "mismatch_markup", label: "Markup Mismatch", accent: "#e65100" },
    { key: "mismatch_price_and_markup", label: "Price & Markup", accent: "#c62828" },
    { key: "unmatched_bgm", label: "Unmatched BGM", accent: "#f57c00" },
    { key: "unmatched_acers", label: "Unmatched ACERS", accent: "#f57c00" },
    { key: "canon_failed", label: "Canon Failed", accent: "#555" },
  ];

const INPUT_SX = {
  "& .MuiInputBase-root": { fontSize: "0.7rem", height: "32px" },
  "& .MuiInputLabel-root": {
    fontSize: "0.7rem",
    transform: "translate(14px, 8px) scale(1)",
    "&.MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C0C0C0" },
  minWidth: 130,
  flex: "1 1 130px",
  maxWidth: 200,
} as const;

const COMMON_TABLE_PROPS = {
  enableDensityToggle: false,
  enableColumnOrdering: true,
  enableColumnFilterModes: false,
  enableColumnPinning: true,
  initialState: { showColumnFilters: false, density: "compact" as const },
  muiTableHeadCellProps: { sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal", fontSize: "0.72rem" } },
  muiTableBodyCellProps: { sx: { border: "1px solid #ebe9e98f", fontSize: "0.73rem" } },
  muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0" } },
  muiTableContainerProps: { sx: { backgroundColor: "#fff" } },
  muiTopToolbarProps: {
    className: "top-toolbar-wrapper__new",
    sx: { backgroundColor: "#fff", padding: "8px" },
  },
  muiBottomToolbarProps: {
    className: "bottom-toolbar-wrapper__new",
    sx: { backgroundColor: "#fff", padding: "8px" },
  },

} as const;


const SummaryCard: React.FC<{ label: string; value: number; accent?: string }> = ({
  label,
  value,
  accent = "#00bcd4",
}) => (
  <Box
    sx={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", p: "10px 14px", borderRadius: "6px",
      border: `1px solid ${accent}40`, backgroundColor: `${accent}0d`,
      minWidth: 80, flex: "1 1 80px",
    }}
  >
    <Typography sx={{ fontSize: "1.35rem", fontWeight: 700, lineHeight: 1, color: accent }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "0.6rem", color: "#777", mt: 0.5, textAlign: "center", lineHeight: 1.2 }}>
      {label}
    </Typography>
  </Box>
);

const MismatchCell: React.FC<{
  bgm: number | null;
  acers: number | null;
  highlight: boolean;
}> = ({ bgm, acers, highlight }) => (
  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
    {(["BGM", "ACERS"] as const).map((src) => {
      const val = src === "BGM" ? bgm : acers;
      const hilit = src === "ACERS" && highlight;
      return (
        <Box key={src}>
          <Typography sx={{ fontSize: "0.58rem", color: "#aaa" }}>{src}</Typography>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: hilit ? "#c62828" : "inherit" }}>
            {val ?? "—"} {hilit && "🔴"}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const StatusChip: React.FC<{ status: string }> = ({ status }) => (
  <Chip
    label={STATUS_LABEL[status] ?? status}
    color={STATUS_COLOR[status] ?? "default"}
    size="small"
    sx={{ fontSize: "0.62rem", height: 20 }}
  />
);

const BreakReasonCell: React.FC<{ value: ReconTradeRow["break_reason"]; showRow?: boolean }> = ({ value, showRow = false }) => {
  if (!value) return null;

  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object") {
    const items = value as BreakReasonItem[];
    return (
      <Typography sx={{ fontSize: "0.72rem", color: "#c62828" }}>
        {items.map(item => showRow ? `Row ${item.row}: ${item.reason}` : item.reason).join(", ")}
      </Typography>
    );
  }
  return (
    <Typography sx={{ fontSize: "0.72rem", color: "#c62828" }}>
      {Array.isArray(value) ? value.join(", ") : value}
    </Typography>
  );
};

const normalizeStatus = (status?: string | null) =>
  (status ?? "").trim().toUpperCase();

const splitCanonicalisationStatuses = (status?: string | null) =>
  normalizeStatus(status)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const CANONICALISATION_STATUS_LABELS: Record<string, string> = {
  UNSUPPORTED_DATE_FORMAT_TRADE_DATE: "Unsupported Trade Date Format",
  UNSUPPORTED_DATE_FORMAT_CONTRACT_MONTH: "Unsupported Contract Month Format",
  INVALID_DIRECTION: "Invalid Direction",
  FAILED_MISSING_CLIENT_MAPPING: "Client Mapping Failed",
  FAILED_MISSING_CONTRACT_MAPPING: "Contract Mapping Failed",
  FAILED_MISSING_CLIENT_MAPPING_AND_CONTRACT_MAPPING: "Client & Contract Mapping Failed",
  INVALID_QUANTITY: "Invalid Quantity",
};

const formatCanonicalisationStatus = (status?: string | null) => {
  const statuses = splitCanonicalisationStatuses(status);
  if (!statuses.length) return "—";

  return statuses.map((item) => CANONICALISATION_STATUS_LABELS[item] ?? item).join(", ");
};

const isBreakReasonItem = (item: unknown): item is BreakReasonItem => {
  if (!item || typeof item !== "object") return false;

  const candidate = item as Partial<BreakReasonItem>;
  return (
    typeof candidate.row === "number" &&
    typeof candidate.value === "string" &&
    typeof candidate.reason === "string"
  );
};

const getBreakReasonItems = (value: ReconTradeRow["break_reason"]): BreakReasonItem[] => {
  if (!Array.isArray(value)) return [];

  const items: unknown[] = value;
  return items.filter(isBreakReasonItem);
};

const getFailureFieldFallback = (
  row: ReconTradeRow,
  field: "trade_date" | "contract_month" | "direction" | "client" | "contract" | "quantity"
): string | number | null => {
  const statuses = splitCanonicalisationStatuses(row.canonicalisation_status);
  const reasons = getBreakReasonItems(row.break_reason);

  if (
    field === "trade_date" &&
    statuses.includes("UNSUPPORTED_DATE_FORMAT_TRADE_DATE")
  ) {
    return reasons.find((item) => item.reason === "Unsupported Date Format For Trade Date.Use DD-MM-YYYY or DD-MM-YY")?.value ?? null;
  }

  if (
    field === "contract_month" &&
    statuses.includes("UNSUPPORTED_DATE_FORMAT_CONTRACT_MONTH")
  ) {
    return reasons.find((item) => item.reason === "Unsupported Date Format for Contract Month.Use DD-MM-YYYY or DD-MM-YY")?.value ?? null;
  }

  if (
    field === "client" &&
    (
      statuses.includes("FAILED_MISSING_CLIENT_MAPPING_AND_CONTRACT_MAPPING") ||
      statuses.includes("FAILED_MISSING_CLIENT_MAPPING")
    )
  ) {
    return reasons.find((item) => item.reason === "Customer mapping failed")?.value ?? null;
  }

  if (
    field === "contract" &&
    (
      statuses.includes("FAILED_MISSING_CLIENT_MAPPING_AND_CONTRACT_MAPPING") ||
      statuses.includes("FAILED_MISSING_CONTRACT_MAPPING")
    )
  ) {
    return reasons.find((item) => item.reason === "Contract mapping failed")?.value ?? null;
  }

  if (field === "quantity" && statuses.includes("INVALID_QUANTITY")) {
    return reasons.find((item) => item.reason === "Invalid quantity")?.value ?? null;
  }

  if (field === "direction" && statuses.includes("INVALID_DIRECTION")) {
    return reasons.find((item) => item.reason === "Invalid direction")?.value ?? null;
  }

  return null;
};


/** Reusable info-icon cell that opens the detail modal */
const makeDetailColumn = (
  onOpen: (row: ReconTradeRow) => void
): MRT_ColumnDef<ReconTradeRow> => ({
  id: "detail",
  header: "",
  size: 30,
  minSize: 30,
  maxSize: 30,
  enableResizing: false,
  enableSorting: false,
  enableColumnActions: false,
  enableColumnDragging: false,
  enableColumnOrdering: false,
  enableHiding: false,
  enablePinning: false,
  muiTableHeadCellProps: { sx: { padding: "0 4px" } },
  muiTableBodyCellProps: { sx: { padding: "0 4px" } },
  Cell: ({ row }) => (
    <InfoOutlinedIcon
      sx={{ fontSize: 16, color: "#00bcd4", cursor: "pointer" }}
      onClick={() => onOpen(row.original)}
    />
  ),
});

const makeMismatchColumn = (
  field: "price" | "markup"
): MRT_ColumnDef<ReconTradeRow> => ({
  id: field,
  header: field === "price" ? "Price" : "Markup",
  size: 160,
  Cell: ({ row }) => (
    <MismatchCell
      bgm={field === "price" ? row.original.bgm_price : row.original.markup_bgm}
      acers={field === "price" ? row.original.acers_price : row.original.markup_acers}
      highlight={
        row.original.reconciliation_status === (field === "price" ? "MISMATCH_PRICE" : "MISMATCH_MARKUP") ||
        row.original.reconciliation_status === "MISMATCH_PRICE_AND_MARKUP"
      }
    />
  ),
});


const ReconciliationAutomationPage: React.FC = () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const [sourceOptions, setSourceOptions] = useState<SelectOption[]>([]);
  const [contractOptions, setContractOptions] = useState<SelectOption[]>([]);
  const [filters, setFilters] = useState<ReconFilterFormData>(INITIAL_FILTERS);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [summary, setSummary] = useState<ReconSummary | null>(null);
  const [results, setResults] = useState<ReconTradeRow[]>([]);
  const [activeTab, setActiveTab] = useState<ActiveTab>("all");
  const [unmatchedSubTab, setUnmatchedSubTab] = useState<"bgm" | "acers">("bgm");
  const [modalTrade, setModalTrade] = useState<ReconTradeRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [formatErrors, setFormatErrors] = useState<ValidationError[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Callbacks ──────────────────────────────────────────────────────────────

  const openModal = useCallback((trade: ReconTradeRow) => {
    setModalTrade(trade);
    setModalOpen(true);
  }, []);

  const updateFilter = useCallback((key: keyof ReconFilterFormData, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClear = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setSelectedFile(null);
    setSummary(null);
    setResults([]);
    setHasRun(false);
    setRunId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = "." + file.name.split(".").pop()?.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      toast.error(`Unsupported file format "${ext}". Please upload .xlsx, .xlsm, or .csv only.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setSelectedFile(file);
  }, []);

  const applyResults = useCallback((data: ReconRunResponse) => {
    setSummary(data.summary);
    setResults(data.results);
    setRunId(data.run_id);
    setHasRun(true);
    setActiveTab("all");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleRun = useCallback(async () => {
    if (!filters.start_date || !filters.end_date) return toast.error("Please select a date range.");
    if (!filters.source) return toast.error("Please select a source.");
    if (!selectedFile) return toast.error("Please upload a BGM broker file.");
    setIsRunning(true);
    try {
         const data = await runReconciliation({
           source: filters.source,
           start_date: filters.start_date,
           end_date: filters.end_date,
           file: selectedFile,
           ...(filters.contract && { contract: filters.contract }),
           ...(filters.buy_sell && { buy_sell: filters.buy_sell as BuySell }),
         });
      applyResults(data);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Reconciliation completed successfully!");
    } catch (err: any) {
      const details = err?.response?.data?.details;
      if (details?.status === "partial" && details?.errors?.length) {
        setFormatErrors(details.errors);
      } else {
        toast.error("Reconciliation failed. Please try again.");
        console.error(err);
      }
    } finally {
      setIsRunning(false);
    }
  }
  , [filters, selectedFile, applyResults]);


  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const source = e.currentTarget;

    const containers = document.querySelectorAll<HTMLDivElement>(".paired-table-container");
    containers.forEach(el => {
      if (el !== source) {
        el.scrollLeft = source.scrollLeft;
        el.scrollTop = source.scrollTop;
      }
    });
  }, []);


  useEffect(() => {
    fetchBrokerOptions().then(({ data, error }) => {
      if (error || !Array.isArray(data)) {
        toast.error(`Error loading sources: ${error ?? "Unknown error"}`);
        setSourceOptions([]);
      } else {
        const opts = data.map((item: BrokerOption) => ({ value: item.name, label: item.name }));
        setSourceOptions(opts);
        setFilters(prev => ({ ...prev, source: DEFAULT_SOURCE })); 
      }
    });

    fetchContractOptions()
      .then((opts) => {
        setContractOptions([CONTRACT_ALL_OPTION, ...opts]);
      })
      .catch(() => toast.error("Error loading contract options"));
  }, []);
  const handleDownloadTemplate = () => {
    const a = document.createElement("a");
    a.href = "/templates/recon-template.csv";
    a.download = "recon-template.csv";
    a.click();
  };

  const rows = useMemo(() => ({
    all: results,
    matched: results.filter((r) => r.reconciliation_status === "MATCHED"),
    mismatch: results.filter((r) => ["MISMATCH_PRICE", "MISMATCH_MARKUP", "MISMATCH_PRICE_AND_MARKUP"].includes(r.reconciliation_status)),
    unmatchedBgm: results.filter((r) => r.reconciliation_status === "UNMATCHED_BGM"),
    unmatchedAcers: results.filter((r) => r.reconciliation_status === "UNMATCHED_ACERS"),
    failures: results.filter((r) => r.canonicalisation_status !== "SUCCESS"),
  }), [results]);


  const detailCol = useMemo(() => makeDetailColumn(openModal), [openModal]);

  const columns = useMemo(() => {
    const base: MRT_ColumnDef<ReconTradeRow>[] = [
      { accessorKey: "trade_date", header: "Trade Date", size: 90 },
      { accessorKey: "source_trade_id", header: "Trade ID", size: 100 },
      { accessorKey: "client", header: "Client", size: 80 },
      { accessorKey: "contract", header: "Contract", size: 90 },
      { accessorKey: "quantity", header: "Quantity", size: 60 },
    ];

    const contractMonthCol: MRT_ColumnDef<ReconTradeRow> = {
      accessorKey: "contract_month",
      header: "Contract Month",
      size: 90,
    };


    const statusCol: MRT_ColumnDef<ReconTradeRow> = {
      accessorKey: "reconciliation_status",
      header: "Status",
      size: 140,
      Cell: ({ cell }) => <StatusChip status={cell.getValue<string>()} />,
    };

    const breakCol: MRT_ColumnDef<ReconTradeRow> = {
      accessorKey: "break_reason",
      header: "Break Reason",
      size: 260,
      Cell: ({ cell }) => <BreakReasonCell value={cell.getValue<ReconTradeRow["break_reason"]>()} />,
    };

    const failureBase: MRT_ColumnDef<ReconTradeRow>[] = base.map((col) => {
      if (col.accessorKey === "trade_date") {
        return {
          ...col,
          Cell: ({ row }) => (
            <Typography sx={{ fontSize: "0.72rem" }}>
              {row.original.trade_date || getFailureFieldFallback(row.original, "trade_date") || "—"}
            </Typography>
          ),
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      if (col.accessorKey === "client") {
        return {
          ...col,
          Cell: ({ row }) => {
            return (
              <Typography sx={{ fontSize: "0.72rem" }}>
                {row.original.client || getFailureFieldFallback(row.original, "client") || "—"}
              </Typography>
            );
          },
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      if (col.accessorKey === "contract") {
        return {
          ...col,
          Cell: ({ row }) => {
            return (
              <Typography sx={{ fontSize: "0.72rem" }}>
                {row.original.contract || getFailureFieldFallback(row.original, "contract") || "—"}
              </Typography>
            );
          },
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      if (col.accessorKey === "quantity") {
        return {
          ...col,
          Cell: ({ row }) => (
            <Typography sx={{ fontSize: "0.72rem" }}>
              {row.original.quantity ?? getFailureFieldFallback(row.original, "quantity") ?? "—"}
            </Typography>
          ),
        } as MRT_ColumnDef<ReconTradeRow>;
      }

      return col;
    });

    failureBase.push(
      {
        ...contractMonthCol,
        Cell: ({ row }) => (
          <Typography sx={{ fontSize: "0.72rem" }}>
            {row.original.contract_month || getFailureFieldFallback(row.original, "contract_month") || "—"}
          </Typography>
        ),
      } as MRT_ColumnDef<ReconTradeRow>,
      {
        accessorKey: "direction",
        header: "Direction",
        size: 90,
        Cell: ({ row }) => (
          <Typography sx={{ fontSize: "0.72rem" }}>
            {row.original.direction || getFailureFieldFallback(row.original, "direction") || "—"}
          </Typography>
        ),
      } as MRT_ColumnDef<ReconTradeRow>,
    );

    return {
      all: [
         detailCol,
        { accessorKey: "trade_date", header: "Trade Date", size: 90 },
        { accessorKey: "source_system", header: "Source", size: 70 },
        { accessorKey: "source_trade_id", header: "Trade ID", size: 100 },
        { accessorKey: "client", header: "Client", size: 80 },
        { accessorKey: "contract", header: "Contract", size: 90 },
        { accessorKey: "contract_month", header: "Contract Month", size: 90 },
        { accessorKey: "quantity", header: "Quantity", size: 60 },
        { accessorKey: "price", header: "Price", size: 100 },
        { accessorKey: "bgm_price", header: "Price (BGM)", size: 100 },
        { accessorKey: "acers_price", header: "Price (ACERS)", size: 110 },
        // { ...makeMismatchColumn("price"), size: 150 },
        statusCol,
        { accessorKey: "matched_with_id", header: "Matched With ID", size: 110 },
        { accessorKey: "direction", header: "Direction", size: 150 },
        breakCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      matched: [
        ...base,
        { accessorKey: "bgm_price", header: "Price (BGM)", size: 100 },
        { accessorKey: "acers_price", header: "Price (ACERS)", size: 110 },
        statusCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      mismatch: [
        ...base,
        makeMismatchColumn("price"),
        makeMismatchColumn("markup"),
        statusCol,
        breakCol,
        detailCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      unmatched: [
        ...base,
        contractMonthCol,
        statusCol,
        breakCol,
      ] as MRT_ColumnDef<ReconTradeRow>[],

      failures: [
        ...failureBase,
        {
          accessorKey: "break_reason",
          header: "Uploaded File Row",
          size: 70,
          Cell: ({ cell }) => {
            const val = cell.getValue<ReconTradeRow["break_reason"]>();
            if (!val || !Array.isArray(val) || typeof val[0] !== "object") {
              return <Typography sx={{ fontSize: "0.72rem" }}>—</Typography>;
            }

            const rows = Array.from(new Set((val as BreakReasonItem[]).map((item) => item.row)));
            return <Typography sx={{ fontSize: "0.72rem", color: "#555" }}>{rows.join(", ")}</Typography>;
          },
        },
        { accessorKey: "source_system", header: "Source", size: 80 },
        {
          accessorKey: "canonicalisation_status",
          header: "Canon Status",
          size: 200,
          Cell: ({ cell }) => (
            <Typography sx={{ fontSize: "0.72rem", color: "#c62828", fontWeight: 600 }}>
              {cell.getValue<string>() ?? "—"}
            </Typography>
          ),
        },
        {
          accessorKey: "break_reason",
          header: "Issue",
          size: 260,
          Cell: ({ cell }) => <BreakReasonCell value={cell.getValue<ReconTradeRow["break_reason"]>()} />,
        },
      ] as MRT_ColumnDef<ReconTradeRow>[],
    };
  }, [detailCol]);


  const unmatchedBgmTable = useMaterialReactTable({ ...COMMON_TABLE_PROPS, columns: columns.unmatched, data: rows.unmatchedBgm, getRowId: (r) => r.id });
  const unmatchedAcersTable = useMaterialReactTable({ ...COMMON_TABLE_PROPS, columns: columns.unmatched, data: rows.unmatchedAcers, getRowId: (r) => r.id });
  const failureTable = useMaterialReactTable({ ...COMMON_TABLE_PROPS, columns: columns.failures, data: rows.failures, getRowId: (r) => r.id });


  const tabLabel = useCallback(
    (label: string, count: number) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {label}
        {hasRun && (
          <Box sx={{ fontSize: "0.62rem", px: 0.7, py: 0.1, borderRadius: "10px", backgroundColor: "#00bcd420", color: "#007b8c", fontWeight: 700, lineHeight: 1.6 }}>
            {count}
          </Box>
        )}
      </Box>
    ),
    [hasRun]
  );


  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div className="table-ad">

            {/* ── Filters & Actions ── */}
            <Accordion defaultActiveKey="0" className="table-ad__addInputs">
              <Accordion.Item eventKey="0">
                <Accordion.Header className="accordion_button">
                  Reconciliation — Filters & Actions
                </Accordion.Header>
                <Accordion.Body>
                  <Box
                    sx={{
                      width: "100%",
                      p: 1.5,
                      backgroundColor: "#fff",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                    }}
                  >
                    {/* Single aligned row: all inputs + file + buttons */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "flex-end",
                        gap: 1,
                      }}
                    >
                      {/* ── Inputs ── */}
                      <CompactSelect
                        label="Source"
                        value={filters.source}
                        onChange={(v) => updateFilter("source", v)}
                        options={sourceOptions}
                        name="source"
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={INPUT_SX}
                      />

                      {(["start_date", "end_date"] as const).map((key) => (
                        <CompactTextField
                          key={key}
                          label={key === "start_date" ? "Trade Start Date" : "Trade End Date"}
                          value={filters[key]}
                          onChange={(v) => updateFilter(key, v)}
                          name={key}
                          type="date"
                          slotProps={{ inputLabel: { shrink: true } }}
                          sx={INPUT_SX}
                        />
                      ))}
                      
                      <CompactSelect
                        label="Contract"
                        value={filters.contract}
                        onChange={(v) => updateFilter("contract", v)}
                        options={contractOptions}
                        name="contract"
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={INPUT_SX}
                      />

                      <CompactSelect
                        label="Buy / Sell"
                        value={filters.buy_sell}
                        onChange={(v) => updateFilter("buy_sell", v)}
                        options={BUY_SELL_OPTIONS}
                        name="buy_sell"
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={INPUT_SX}
                      />

                      {/* ── Divider ── */}
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

                      {/* ── File upload ── */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.3 }}>
                        <Typography sx={{ fontSize: "0.62rem", color: "#777" }}>
                          BGM File{selectedFile && ` — ${selectedFile.name}`}
                        </Typography>
                        <Button
                          component="label"
                          variant="outlined"
                          size="small"
                          startIcon={<UploadFileIcon sx={{ fontSize: 14 }} />}
                          sx={{
                            height: 32,
                            textTransform: "none",
                            fontSize: "0.72rem",
                            minWidth: 120,
                            borderColor: selectedFile ? "#00bcd4" : "#C0C0C0",
                            color: selectedFile ? "#00bcd4" : "inherit",
                          }}
                        >
                          {selectedFile ? "Change File" : "Choose File"}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,.xlsx,xlsm"
                            hidden
                            onChange={handleFileChange}
                          />
                        </Button>
                      </Box>
                      <Button
                        onClick={handleDownloadTemplate}
                        variant="outlined"
                        size="small"
                        startIcon={<GetAppIcon sx={{ fontSize: 14 }} />}
                        sx={{
                          height: 32,
                          textTransform: "none",
                          fontSize: "0.72rem",
                          minWidth: 120,
                          color: "#777",
                          borderColor: "#C0C0C0",
                        }}
                      >
                        Download Template
                      </Button>
                      {/* ── Divider ── */}
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

                      {/* ── Action buttons — fixed height & consistent sizing ── */}
                      <Button
                        onClick={handleRun}
                        variant="contained"
                        disabled={isRunning || !selectedFile}
                        startIcon={
                          !isRunning && (
                            hasRun
                              ? <ReplayIcon sx={{ fontSize: 15 }} />
                              : <PlayArrowIcon sx={{ fontSize: 15 }} />
                          )
                        }
                        sx={{
                          height: 32,
                          backgroundColor: "#00bcd4",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          minWidth: 150,
                          "&:hover": { backgroundColor: "#008c9e" },
                          "&.Mui-disabled": { backgroundColor: "#e0e0e0" },
                        }}
                      >
                        {isRunning ? "Running…" : hasRun ? "Re-run" : "Run Reconciliation"}
                      </Button>

                      {/* <Button
                        onClick={handleReRun}
                        variant="outlined"
                        disabled={isRunning || !hasRun}
                        startIcon={<ReplayIcon sx={{ fontSize: 15 }} />}
                        sx={{
                          height: 32,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          minWidth: 100,
                        }}
                      >
                        Re-run
                      </Button> */}

                      <Button
                        onClick={handleClear}
                        variant="outlined"
                        color="inherit"
                        sx={{
                          height: 32,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          minWidth: 80,
                          color: "#777",
                          borderColor: "#C0C0C0",
                        }}
                      >
                        Clear
                      </Button>
                    </Box>

                    {/* ── Session hint ── */}
                    {hasRun && (
                      <Typography sx={{ fontSize: "0.65rem", color: "#00897b", mt: 1 }}>
                        ✓ File in session — Re-run will reuse the uploaded file without re-uploading.
                      </Typography>
                    )}
                  </Box>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            {/* ── Summary ── */}
            {summary && (
              <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 1, mb: 1 }}>
                  Summary
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {SUMMARY_CARDS.map(({ key, label, accent }) => (
                    <SummaryCard key={key} label={label} value={summary[key] as number} accent={accent} />
                  ))}
                </Box>
              </Box>
            )}

            {/* ── Results (Tabs) ── */}
            {hasRun && (
              <Box sx={{ mt: 1.5, backgroundColor: "#fff", border: "1px solid #e0e0e0", borderRadius: "4px" }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{ borderBottom: "1px solid #e0e0e0", minHeight: 36, "& .MuiTab-root": { minHeight: 36, fontSize: "0.72rem", textTransform: "none", py: 0 } }}
                >
                  <Tab value={ActiveTabEnum.ALL} label={tabLabel("All", rows.all.length)} />
                  <Tab value={ActiveTabEnum.MATCHED} label={tabLabel("Matched", rows.matched.length)} />
                  <Tab value={ActiveTabEnum.MISMATCHES} label={tabLabel("Mismatches", rows.mismatch.length)} />
                  <Tab value={ActiveTabEnum.UNMATCHED} label={tabLabel("Unmatched", rows.unmatchedBgm.length + rows.unmatchedAcers.length)} />
                  <Tab value={ActiveTabEnum.FAILURES} label={tabLabel("Failures", rows.failures.length)} />
                </Tabs>

                {(activeTab === ActiveTabEnum.ALL || activeTab === ActiveTabEnum.MATCHED || activeTab === ActiveTabEnum.MISMATCHES) && (() => {
                  const activeData =
                    activeTab === ActiveTabEnum.ALL ? results :
                      activeTab === ActiveTabEnum.MATCHED ? rows.matched :
                        rows.mismatch;

                  type PairedRow = { acers: ReconTradeRow | null; bgm: ReconTradeRow | null };

                  const acersAll = activeData.filter(r => r.source_system === ReconSourceEnum.ACERS);
                  const bgmAll = activeData.filter(r => r.source_system !== ReconSourceEnum.ACERS);


                  const matchedPairs: PairedRow[] = acersAll
                    .filter(a => PAIRED_STATUSES.has(a.reconciliation_status) && a.source_trade_id)
                    .map(a => ({
                      acers: a,
                      bgm: bgmAll.find(b => b.matched_with_id === a.source_trade_id) ?? null,
                    }));

                  const pairedAcersIds = new Set(matchedPairs.map(p => p.acers?.source_trade_id).filter(Boolean));
                  const pairedBgmIds = new Set(matchedPairs.map(p => p.bgm?.matched_with_id).filter(Boolean));

                  const unmatchedAcersPairs: PairedRow[] = acersAll
                    .filter(a => !pairedAcersIds.has(a.source_trade_id))
                    .map(a => ({ acers: a, bgm: null }));

                  const unmatchedBgmPairs: PairedRow[] = bgmAll
                    .filter(b => !pairedBgmIds.has(b.matched_with_id))
                    .map(b => ({ acers: null, bgm: b }));

                  const pairedRows: PairedRow[] = [...matchedPairs, ...unmatchedAcersPairs, ...unmatchedBgmPairs];

                  // ── Shared column builders ──
                  const directionCell = (getValue: () => string) => (
                    <Typography sx={{
                      fontSize: "0.72rem", fontWeight: 700,
                      color: getValue() === "B" ? "#1565c0" : "#c62828"
                    }}>
                      {getValue()}
                    </Typography>
                  );

                  const emptyCell = <Typography sx={{ fontSize: "0.72rem", color: "#ccc" }}>—</Typography>;

                  const makeCols = (side: "acers" | "bgm"): MRT_ColumnDef<PairedRow>[] => {
                    const get = (row: PairedRow): ReconTradeRow | null => row[side];

                    const col = (
                      key: keyof ReconTradeRow,
                      header: string,
                      size: number,
                      renderFn?: (val: any, row: ReconTradeRow) => React.ReactNode
                    ): MRT_ColumnDef<PairedRow> => ({
                      id: `${side}_${key}`,
                      header,
                      size,
                      accessorFn: (row) => get(row)?.[key] ?? null,
                      Cell: ({ row }) => {
                        const r = get(row.original);
                        if (!r) return emptyCell;
                        const val = r[key];
                        return renderFn
                          ? <>{renderFn(val, r)}</>
                          : <Typography sx={{ fontSize: "0.73rem", color: "var(--color-text-primary)" }}>
                            {typeof val === "object" && val !== null
                              ? Array.isArray(val)
                                ? (val as any[]).map(v => typeof v === "object" ? v.reason : String(v)).join(", ")
                                : JSON.stringify(val)
                              : (val ?? "—")}
                          </Typography>;
                      },
                    });

                    const firstCol: MRT_ColumnDef<PairedRow> = side === "acers"
                      ? col("source_trade_id", "Trade ID", 140)
                      : col("matched_with_id", "Matched With ID", 150);

                    return [
                      {
                        id: `${side}_detail`,
                        header: "",
                        size: 30, minSize: 30, maxSize: 30,
                        enableResizing: false, enableSorting: false,
                        enableColumnActions: false, enableColumnDragging: false,
                        enableColumnOrdering: false, enableHiding: false, enablePinning: false,
                        muiTableHeadCellProps: { sx: { padding: "0 4px" } },
                        muiTableBodyCellProps: { sx: { padding: "0 4px" } },
                        Cell: ({ row }) => {
                          const r = get(row.original);
                          if (!r) return null;
                          return (
                            <InfoOutlinedIcon
                              sx={{ fontSize: 16, color: "#00bcd4", cursor: "pointer" }}
                              onClick={() => openModal(r)}
                            />
                          );
                        },
                      },
                      firstCol,
                      col("trade_date", "Trade Date", 90),
                      col("source_system", "Source", 70),
                      col("client", "Client", 140),
                      col("contract", "Contract", 70),
                      col("contract_month", "Contract Month", 90),
                      col("direction", "Dir", 50, (val) => directionCell(() => val ?? "")),
                      col("quantity", "Qty", 55),
                      col("price", "Price", 80),
                      col("acers_price", "ACERS Price", 90),
                      col("bgm_price", "BGM Price", 90),
                      col("reconciliation_status", "Status", 140, (val) => <StatusChip status={val} />),
                      col("break_reason", "Break Reason", 160, (val) => <BreakReasonCell value={val} />),
                    ];
                  };

                  const SIDE_CONFIGS: Record<"acers" | "bgm", SidedTableConfig> = {
                    acers: { label: "ACERS Trades", accent: "#1565c0", bg: "#e3f2fd", border: "#90caf9", systemFilter: (r) => r.source_system === "ACERS" },
                    bgm: { label: "BGM Trades", accent: "#2e7d32", bg: "#e8f5e9", border: "#a5d6a7", systemFilter: (r) => r.source_system !== "ACERS" },
                  };

                  const PairedSideTable: React.FC<{
                    config: SidedTableConfig;
                    side: "acers" | "bgm";
                    pairedRows: PairedRow[];
                  }> = ({ config, side, pairedRows }) => {
                    const table = useMaterialReactTable<PairedRow>({
                      ...COMMON_TABLE_PROPS,
                      columns: makeCols(side),
                      data: pairedRows,
                      getRowId: (_, i) => String(i),
                      enablePagination: true,
                      enableStickyHeader: true,
                      initialState: {
                        density: "compact",
                        showColumnFilters: false,
                        columnPinning: { left: [`${side}_detail`] },
                        pagination: { pageSize: 50, pageIndex: 0 },
                      },
                      muiTableHeadProps: {
                        sx: {
                          zIndex: 1,
                        },
                      },
                      muiTableHeadCellProps: {
                        sx: {
                          zIndex: 1,
                        },
                      },
                      muiTablePaperProps: { elevation: 0, sx: { borderRadius: 0 } },
                      muiTableContainerProps: { sx: { maxHeight: "400px", overflowY: "auto" },
                      className: "paired-table-container",
                       onScroll: handleScroll,
                    },
                      muiTableBodyRowProps: ({ row }) => {
                        const isMismatch =
                          row.original.acers?.reconciliation_status?.startsWith("MISMATCH") ||
                          row.original.bgm?.reconciliation_status?.startsWith("MISMATCH");

                        const getMismatchColor = () => {
                          if (side === "acers") return "#fff3e0";
                          if (side === "bgm") return "#fce4ec";
                        };

                        return {
                          sx: {
                            backgroundColor: isMismatch
                              ? getMismatchColor()
                              : row.original.acers && row.original.bgm
                                ? "#f1f8f1"
                                : !row.original.bgm
                                  ? "#f0f4ff"
                                  : "#fff3f3",
                          },
                        };
                      },
                      muiTopToolbarProps: {
                        className: "top-toolbar-wrapper__new",
                        sx: { backgroundColor: "#fff", padding: "8px" },
                      },
                      muiBottomToolbarProps: {
                        className: "bottom-toolbar-wrapper__new",
                        sx: { backgroundColor: "#fff", padding: "8px" },
                      },
                    });

                    const tradeCount = pairedRows.filter(p => !!p[side]).length;

                    return (
                      <Box sx={{ flex: 1, minWidth: 0, border: "1px solid #e0e0e0", borderRadius: "4px", overflow: "hidden" }}>
                        <Box sx={{ px: 2, py: 1, background: config.bg, borderBottom: `1px solid ${config.border}` }}>
                          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: config.accent, textTransform: "uppercase", letterSpacing: 1 }}>
                            {config.label} — {tradeCount} trades
                          </Typography>
                        </Box>
                        <MaterialReactTable table={table} />
                      </Box>
                    );
                  };

                  return (
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: "1px solid #e0e0e0", gap: "8px", p: "8px" }}>
                      {(["acers", "bgm"] as const).map(side => (
                        <PairedSideTable
                          key={side}
                          config={SIDE_CONFIGS[side]}
                          side={side}
                          pairedRows={pairedRows}
                        />
                      ))}
                    </Box>
                  );
                })()}

                {/* ── Unmatched — pill toggle, one table at a time ── */}
                {activeTab === "unmatched" && (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, borderBottom: "1px solid #e0e0e0" }}>
                      {(["bgm", "acers"] as const).map((key) => {
                        const count = key === "bgm" ? rows.unmatchedBgm.length : rows.unmatchedAcers.length;
                        const active = unmatchedSubTab === key;
                        const color = key === "bgm" ? "#e65100" : "#1565c0";
                        return (
                          <Box
                            key={key}
                            onClick={() => setUnmatchedSubTab(key)}
                            sx={{
                              cursor: "pointer", px: 1.5, py: 0.4, borderRadius: "20px",
                              fontSize: "0.7rem", fontWeight: active ? 700 : 400,
                              backgroundColor: active ? color : "#f5f5f5",
                              color: active ? "#fff" : "#555",
                              border: `1px solid ${active ? color : "#ddd"}`,
                              transition: "all 0.15s", userSelect: "none",
                              display: "flex", alignItems: "center", gap: 0.5,
                            }}
                          >
                            {key === "bgm" ? "Unmatched BGM" : "Unmatched ACERS"}
                            <Box sx={{ fontSize: "0.6rem", px: 0.6, py: 0.1, borderRadius: "10px", backgroundColor: active ? "rgba(255,255,255,0.25)" : "#e0e0e0", color: active ? "#fff" : "#777", fontWeight: 700, lineHeight: 1.6 }}>
                              {count}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                    {unmatchedSubTab === "bgm" && <MaterialReactTable table={unmatchedBgmTable} />}
                    {unmatchedSubTab === "acers" && <MaterialReactTable table={unmatchedAcersTable} />}
                  </Box>
                )}

                {/* ── Failures ── */}
                {activeTab === "failures" && (
                  <Box>
                    <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                      <Typography sx={{ fontSize: "0.65rem", color: "#e65100" }}>
                        These trades could not be canonicalised and are excluded from reconciliation results until the mapping issue is resolved.
                      </Typography>
                    </Box>
                    <MaterialReactTable table={failureTable} />
                  </Box>
                )}
              </Box>
            )}

          </div>
        </Row>
      </Container>
      {formatErrors && (
        <DateFormatErrorModal
          errors={formatErrors}
          onClose={() => setFormatErrors(null)}
        />
      )}
      <TradeDetailModal open={modalOpen} onClose={() => setModalOpen(false)} trade={modalTrade} />
    </div>
  );
};

export default ReconciliationAutomationPage;